import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Users, Plus, Trash2, ShieldCheck, Shield, X, Copy, Check, Plug, KeyRound } from 'lucide-react';
import { ATSIntegrationPanel } from '../components/ATSIntegrationPanel';

export const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [copiedPwd, setCopiedPwd] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', role: 'user' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès réservé aux administrateurs');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.nom || !form.prenom) {
      toast.error('Tous les champs sont obligatoires');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/admin/users', form);
      setCreatedInfo(res.data);
      setForm({ prenom: '', nom: '', email: '', role: 'user' });
      fetchUsers();
      if (res.data.email_sent) {
        toast.success('Compte créé — email envoyé');
      } else {
        toast.success('Compte créé — email non envoyé (transmettez le mot de passe manuellement)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      toast.success('Rôle mis à jour');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Supprimer le compte ${email} ?`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Compte supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetPassword = async (userId, email) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${email} ?`)) return;
    try {
      const res = await api.post(`/admin/users/${userId}/reset-password`);
      setResetInfo({ ...res.data, email });
      fetchUsers();
      if (res.data.email_sent) {
        toast.success('Mot de passe réinitialisé — email envoyé');
      } else {
        toast.success('Mot de passe réinitialisé — transmettez-le manuellement');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const copyPassword = (pwd) => {
    navigator.clipboard.writeText(pwd);
    setCopiedPwd(true);
    setTimeout(() => setCopiedPwd(false), 2000);
  };

  const currentUser = JSON.parse(sessionStorage.getItem('eva_user') || '{}');

  return (
    <Layout title="Administration" breadcrumbs={['Accueil', 'Administration']}>
      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/10" data-testid="admin-tabs">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'users'
              ? 'border-[#0B132B] text-white'
              : 'border-transparent text-slate-400 hover:text-slate-300'
          }`}
          data-testid="tab-users"
        >
          <Users size={16} />
          Utilisateurs
        </button>
        <button
          onClick={() => setActiveTab('ats')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'ats'
              ? 'border-[#0B132B] text-white'
              : 'border-transparent text-slate-400 hover:text-slate-300'
          }`}
          data-testid="tab-ats"
        >
          <Plug size={16} />
          Intégrations ATS
        </button>
      </div>

      {activeTab === 'users' && (
        <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users size={20} className="text-slate-400" />
          <p className="text-sm text-slate-400">{users.length} utilisateur{users.length > 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setCreatedInfo(null); }}
          className="eva-btn-primary flex items-center gap-2"
          data-testid="create-user-button"
        >
          <Plus size={18} />
          Créer un compte
        </button>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Chargement...</p>
        </div>
      ) : (
        <div className="eva-card overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 font-semibold text-slate-400 uppercase tracking-wider text-xs">Utilisateur</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-400 uppercase tracking-wider text-xs">Email</th>
                <th className="text-center py-3 px-4 font-semibold text-slate-400 uppercase tracking-wider text-xs">Rôle</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-400 uppercase tracking-wider text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#F8F9FA] hover:bg-white/5 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4">
                    <p className="font-medium text-white">{u.prenom} {u.nom}</p>
                    {u.must_change_password && (
                      <span className="text-xs text-[#F59E0B]">Mot de passe provisoire</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-300">{u.email}</td>
                  <td className="text-center py-3 px-4">
                    <select
                      value={u.role || 'user'}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      disabled={u.id === currentUser.id}
                      className={`text-xs px-3 py-1 rounded-full font-medium border-0 cursor-pointer ${
                        u.role === 'admin'
                          ? 'bg-[#0B132B] text-white'
                          : 'bg-white/5 text-slate-300'
                      } ${u.id === currentUser.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`role-select-${u.id}`}
                    >
                      <option value="admin">Admin</option>
                      <option value="user">Utilisateur</option>
                    </select>
                  </td>
                  <td className="text-right py-3 px-4">
                    {u.id !== currentUser.id && (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleResetPassword(u.id, u.email)}
                          className="text-[#F59E0B] hover:text-[#D97706] transition-colors p-1"
                          title="Réinitialiser le mot de passe"
                          data-testid={`reset-password-${u.id}`}
                        >
                          <KeyRound size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(u.id, u.email)}
                          className="text-[#EF4444] hover:text-[#DC2626] transition-colors p-1"
                          title="Supprimer"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="create-user-modal">
          <div className="bg-[#0B132B] rounded-lg max-w-md w-full m-4">
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">
                {createdInfo ? 'Compte créé' : 'Créer un compte'}
              </h3>
              <button onClick={() => { setShowCreate(false); setCreatedInfo(null); }} className="text-slate-400 hover:text-white" data-testid="close-create-modal">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {createdInfo ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-emerald-500/15 rounded-full flex items-center justify-center">
                      <Check size={20} className="text-[#10B981]" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{createdInfo.prenom} {createdInfo.nom}</p>
                      <p className="text-sm text-slate-400">{createdInfo.email}</p>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Mot de passe provisoire</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-[#0B132B] border border-white/10 px-3 py-2 rounded text-white font-mono" data-testid="temp-password-display">
                        {createdInfo.temp_password}
                      </code>
                      <button
                        onClick={() => copyPassword(createdInfo.temp_password)}
                        className="p-2 text-slate-400 hover:text-white transition-colors"
                        data-testid="copy-password-button"
                      >
                        {copiedPwd ? <Check size={16} className="text-[#10B981]" /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>

                  {createdInfo.email_sent ? (
                    <p className="text-sm text-[#10B981] flex items-center gap-2">
                      <ShieldCheck size={16} />
                      Email envoyé à {createdInfo.email}
                    </p>
                  ) : (
                    <p className="text-sm text-[#F59E0B] flex items-center gap-2">
                      <Shield size={16} />
                      Email non envoyé — transmettez le mot de passe manuellement
                    </p>
                  )}

                  <button
                    onClick={() => { setShowCreate(false); setCreatedInfo(null); }}
                    className="eva-btn-primary w-full mt-4"
                  >
                    Fermer
                  </button>
                </div>
              ) : (
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="eva-label">Prénom</label>
                      <input
                        type="text"
                        value={form.prenom}
                        onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                        className="eva-input"
                        required
                        data-testid="create-prenom-input"
                      />
                    </div>
                    <div>
                      <label className="eva-label">Nom</label>
                      <input
                        type="text"
                        value={form.nom}
                        onChange={(e) => setForm({ ...form, nom: e.target.value })}
                        className="eva-input"
                        required
                        data-testid="create-nom-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="eva-label">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="eva-input"
                      required
                      data-testid="create-email-input"
                    />
                  </div>
                  <div>
                    <label className="eva-label">Rôle</label>
                    <select
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className="eva-input"
                      data-testid="create-role-select"
                    >
                      <option value="user">Utilisateur</option>
                      <option value="admin">Administrateur</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={creating}
                    className="eva-btn-primary w-full"
                    data-testid="submit-create-user"
                  >
                    {creating ? 'Création...' : 'Créer le compte'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Reset Password Modal */}
      {resetInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="reset-password-modal">
          <div className="bg-[#0B132B] rounded-lg max-w-md w-full m-4">
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Mot de passe réinitialisé</h3>
              <button onClick={() => setResetInfo(null)} className="text-slate-400 hover:text-white" data-testid="close-reset-modal">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#F59E0B]/15 rounded-full flex items-center justify-center">
                  <KeyRound size={20} className="text-[#F59E0B]" />
                </div>
                <p className="text-sm text-slate-300">{resetInfo.email}</p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Nouveau mot de passe provisoire</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#0B132B] border border-white/10 px-3 py-2 rounded text-white font-mono" data-testid="reset-password-display">
                    {resetInfo.temp_password}
                  </code>
                  <button
                    onClick={() => copyPassword(resetInfo.temp_password)}
                    className="p-2 text-slate-400 hover:text-white transition-colors"
                    data-testid="copy-reset-password-button"
                  >
                    {copiedPwd ? <Check size={16} className="text-[#10B981]" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              {resetInfo.email_sent ? (
                <p className="text-sm text-[#10B981] flex items-center gap-2">
                  <ShieldCheck size={16} />
                  Email de réinitialisation envoyé
                </p>
              ) : (
                <p className="text-sm text-[#F59E0B] flex items-center gap-2">
                  <Shield size={16} />
                  Email non envoyé — transmettez le mot de passe manuellement
                </p>
              )}

              <button
                onClick={() => setResetInfo(null)}
                className="eva-btn-primary w-full mt-4"
                data-testid="close-reset-button"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {activeTab === 'ats' && (
        <ATSIntegrationPanel />
      )}
    </Layout>
  );
};
